import '../i18n/autoTranslate';
import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import Header from '../../components/ui/Header';
import QuickActionButton from '../../components/ui/QuickActionButton';
import OverallScoreCard from './components/OverallScoreCard';
import PerformanceBreakdown from './components/PerformanceBreakdown';
import DetailedFeedbackPanel from './components/DetailedFeedbackPanel';
import AnswerReviewSection from './components/AnswerReviewSection';
import ImprovementSuggestions from './components/ImprovementSuggestions';
import NextStepsSection from './components/NextStepsSection';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import SpotlightCard from '../../components/ui/SpotlightCard';
import AppBackground from '../../components/ui/AppBackground.tsx';


import { API_BASE_URL, getAccessToken, getAuthHeaders } from '../../config/api';

const InterviewResultsFeedback = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [showShareModal, setShowShareModal] = useState(false);
  const [sessionData, setSessionData] = useState(null);
  const [detailData, setDetailData] = useState({
    answerReviews: [],
    detailedFeedback: [],
    improvementSuggestions: [],
    nextSteps: {}
  });

  useEffect(() => {
    const timer = setTimeout(() => {
      const stateSession = location?.state?.sessionData || null;
      const liveSessionRaw = localStorage.getItem('lastInterviewSession');
      const phoneResultsRaw = localStorage.getItem('interviewResults');
      const interviewId = localStorage.getItem('current_interview_id');

      let normalized = null;
      let answerReviews = [];
      let detailedFeedback = [];
      let improvementSuggestions = [];
      let nextSteps = {};

      if (stateSession) {
        normalized = {
          sessionId: stateSession.sessionId,
          sessionType: `${stateSession.selectedType || 'Interview'} - ${stateSession.selectedRole || 'General'}`,
          duration: `${stateSession.duration || 0} minutes`,
          completedAt: stateSession.completedAt ? new Date(stateSession.completedAt) : new Date(),
          overallScore: Math.round(stateSession.completionRate || 0),
          categories: [
            { name: 'Completion', score: Math.round(stateSession.completionRate || 0), maxScore: 100, description: 'Questions completed' }
          ],
          weakestAreas: [],
          strengths: []
        };
      } else if (interviewId && getAccessToken()) {
        fetch(`${API_BASE_URL}/api/interview/${interviewId}`, {
          method: 'GET',
          headers: getAuthHeaders()
        })
          .then(async (r) => {
            if (!r.ok) return null;
            return await r.json();
          })
          .then((data) => {
            const interview = data?.interview || null;
            const results = interview?.results || null;
            if (!results) return null;

            const categoriesDict = results?.scores?.categories || {};
            const categories = Object.entries(categoriesDict).map(([key, value]) => ({
              name: (value?.name || key || '').replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase()),
              score: Math.round(value?.score || 0),
              maxScore: Math.round(value?.max_score || 100),
              description: value?.description || ''
            }));
            const sorted = [...categories].sort((a, b) => a.score - b.score);

            normalized = {
              sessionId: String(interview?.session_id || interviewId),
              sessionType: `${interview?.configuration?.questions?.type || 'Interview'} - ${interview?.configuration?.targeting?.role || 'General'}`,
              duration: results?.session_info?.duration || `${interview?.configuration?.format?.duration || 0} minutes`,
              completedAt: results?.session_info?.completed_at ? new Date(results.session_info.completed_at) : new Date(),
              overallScore: Math.round(results?.scores?.overall_score || 0),
              categories,
              weakestAreas: sorted.slice(0, 2).map((c) => c.name),
              strengths: sorted.slice(-3).reverse().map((c) => c.name)
            };

            answerReviews = results?.answer_reviews || [];
            detailedFeedback = results?.feedback?.detailed_feedback || [];
            improvementSuggestions = results?.improvement_suggestions || [];
            nextSteps = results?.next_steps || {};

            setSessionData(normalized);
            setDetailData({
              answerReviews,
              detailedFeedback,
              improvementSuggestions,
              nextSteps
            });
            setIsLoading(false);
            return true;
          })
          .catch(() => null);
        return;
      } else if (phoneResultsRaw) {
        try {
          const phone = JSON.parse(phoneResultsRaw);
          const m = phone?.metrics || {};
          const overallScore = typeof phone?.finalScore === 'number' ? phone.finalScore : 0;
          const categories = [
            { name: 'Communication', score: Math.round(m.clarity || 0), maxScore: 100, description: 'Clear articulation and expression' },
            { name: 'Professionalism', score: Math.round(m.professionalism || 0), maxScore: 100, description: 'Tone, structure, and presence' },
            { name: 'Confidence', score: Math.round(m.confidence || 0), maxScore: 100, description: 'Voice energy and consistency' },
            { name: 'Pacing', score: Math.round(m.pacing || 0), maxScore: 100, description: 'Speaking pace and cadence' },
            { name: 'Tone', score: Math.round(m.tone || 0), maxScore: 100, description: 'Appropriate emotional tone' }
          ];
          const sorted = [...categories].sort((a, b) => a.score - b.score);
          normalized = {
            sessionId: `PHONE-${new Date(phone?.timestamp || Date.now()).getTime()}`,
            sessionType: `Phone Interview - ${phone?.jobRole || 'General'}`,
            duration: `${Math.round((phone?.answers || []).reduce((s, a) => s + (a?.duration || 0), 0) / 60)} minutes`,
            completedAt: phone?.timestamp ? new Date(phone.timestamp) : new Date(),
            overallScore,
            categories,
            weakestAreas: sorted.slice(0, 2).map(c => c.name),
            strengths: sorted.slice(-3).reverse().map(c => c.name)
          };

          answerReviews = (phone?.answers || []).map((a, idx) => {
            const d = phone?.detailedAnalysis?.[idx] || {};
            return {
              id: idx + 1,
              question: a?.question || '',
              user_answer: a?.transcript || '',
              ai_model_answer: d?.perfectAnswer || '',
              score: typeof d?.aiScore === 'number' ? Math.round(d.aiScore) : typeof d?.rating === 'number' ? Math.round(d.rating * 10) : 0,
              highlights: {
                strengths: d?.strengths ? [String(d.strengths)] : [],
                improvements: d?.improvements ? [String(d.improvements)] : []
              },
              audio_url: null,
              duration: a?.duration ? `${Math.round(a.duration)}s` : ''
            };
          });
          detailedFeedback = [
            {
              id: 'communication',
              title: 'Communication Analysis',
              icon: 'MessageSquare',
              score: Math.round(m.clarity || 0),
              insights: [
                {
                  type: 'positive',
                  title: 'Clarity',
                  description: 'Your responses were clear and understandable.',
                  details: `Clarity score: ${Math.round(m.clarity || 0)}/100`
                }
              ]
            }
          ];
          improvementSuggestions = (phone?.detailedAnalysis || [])
            .map((d, idx) => {
              const text = String(d?.improvements || '').trim();
              if (!text) return null;
              return {
                id: idx + 1,
                category: 'Communication',
                priority: idx === 0 ? 'high' : 'medium',
                title: 'Improve Answer Structure',
                description: text,
                action_items: ['Practice concise structure', 'Add measurable outcomes', 'Use clear transitions'],
                resources: [],
                estimated_time: '1-2 weeks',
                difficulty: 'Medium'
              };
            })
            .filter(Boolean)
            .slice(0, 6);
          nextSteps = {};
        } catch (e) {
          normalized = null;
        }
      } else if (liveSessionRaw) {
        try {
          const live = JSON.parse(liveSessionRaw);
          normalized = {
            sessionId: live.sessionId,
            sessionType: `${live.selectedType || 'Interview'} - ${live.selectedRole || 'General'}`,
            duration: `${live.duration || 0} minutes`,
            completedAt: live.completedAt ? new Date(live.completedAt) : new Date(),
            overallScore: Math.round(live.completionRate || 0),
            categories: [
              { name: 'Completion', score: Math.round(live.completionRate || 0), maxScore: 100, description: 'Questions completed' }
            ],
            weakestAreas: [],
            strengths: []
          };
        } catch (e) {
          normalized = null;
        }
      }

      if (!normalized) {
        normalized = {
          sessionId: 'UNKNOWN',
          sessionType: 'Interview Results',
          duration: '0 minutes',
          completedAt: new Date(),
          overallScore: 0,
          categories: [],
          weakestAreas: [],
          strengths: []
        };
      }

      setSessionData(normalized);
      setDetailData({
        answerReviews,
        detailedFeedback,
        improvementSuggestions,
        nextSteps
      });
      setIsLoading(false);
    }, 500);

    return () => clearTimeout(timer);
  }, [location?.state]);

  const tabs = [
    { id: 'overview', label: 'Overview', icon: 'BarChart3' },
    { id: 'detailed', label: 'Detailed Analysis', icon: 'Search' },
    { id: 'answers', label: 'Answer Review', icon: 'PlayCircle' },
    { id: 'suggestions', label: 'Improvements', icon: 'Target' },
    { id: 'next-steps', label: 'Next Steps', icon: 'ArrowRight' }
  ];

  const handleShareResults = () => {
    setShowShareModal(true);
  };

  const handleDownloadReport = () => {
    // Mock PDF download
    const link = document.createElement('a');
    link.href = '#';
    link.download = `Interview_Report_${sessionData?.sessionId}.pdf`;
    link?.click();
  };

  const handleRetakeInterview = () => {
    navigate('/interview-setup-configuration');
  };

  const renderTabContent = () => {
    const answerReviewQuestions = (detailData?.answerReviews || []).map((q, idx) => ({
      id: q?.id ?? idx + 1,
      question: q?.question || '',
      userAnswer: q?.user_answer ?? q?.userAnswer ?? '',
      aiModelAnswer: q?.ai_model_answer ?? q?.aiModelAnswer ?? '',
      score: q?.score ?? 0,
      highlights: q?.highlights || { strengths: [], improvements: [] },
      audioUrl: q?.audio_url ?? q?.audioUrl ?? null,
      duration: q?.duration ?? ''
    }));

    const improvementSuggestions = (detailData?.improvementSuggestions || []).map((s, idx) => ({
      id: s?.id ?? idx + 1,
      category: s?.category || 'Communication',
      priority: s?.priority || 'medium',
      title: s?.title || 'Improvement',
      description: s?.description || '',
      actionItems: s?.action_items ?? s?.actionItems ?? [],
      resources: s?.resources ?? [],
      estimatedTime: s?.estimated_time ?? s?.estimatedTime ?? '',
      difficulty: s?.difficulty || 'Medium'
    }));

    const recommendedPractice = (detailData?.nextSteps?.recommended_practice || detailData?.nextSteps?.recommendedPractice || []).map((p, idx) => ({
      type: p?.type || `Practice ${idx + 1}`,
      difficulty: p?.difficulty || 'Medium',
      focus: p?.focus || '',
      estimatedTime: p?.estimated_time || p?.estimatedTime || '',
      icon: p?.icon || 'Target'
    }));

    const milestones = detailData?.nextSteps?.milestones || [];

    switch (activeTab) {
      case 'overview':
        return (
          <div className="space-y-8">
            <PerformanceBreakdown 
              categories={sessionData?.categories}
              isLoading={isLoading}
            />
          </div>
        );
      case 'detailed':
        return (
          <DetailedFeedbackPanel feedbackSections={detailData?.detailedFeedback} isLoading={isLoading} />
        );
      case 'answers':
        return (
          <AnswerReviewSection questions={answerReviewQuestions} isLoading={isLoading} />
        );
      case 'suggestions':
        return (
          <ImprovementSuggestions suggestions={improvementSuggestions} isLoading={isLoading} />
        );
      case 'next-steps':
        return (
          <NextStepsSection 
            currentScore={sessionData?.overallScore}
            weakestAreas={sessionData?.weakestAreas}
            recommendedPractice={recommendedPractice}
            milestones={milestones}
            isLoading={isLoading}
          />
        );
      default:
        return null;
    }
  };

  return (
    <AppBackground>
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-10">
        <div className="absolute top-4 right-4 w-32 h-32 bg-gradient-to-br from-purple-500 to-indigo-500 rounded-full blur-3xl" />
        <div className="absolute bottom-4 left-4 w-24 h-24 bg-gradient-to-br from-indigo-500 to-purple-500 rounded-full blur-2xl" />
      </div>
      
      {/* <Header /> */}
      
      {/* Main Content */}
      <main className="pt-10 pb-24 px-4 md:px-6 lg:px-8 relative z-10">
        <div className="max-w-7xl mx-auto space-y-8">
          {/* Overall Score Card */}
          <SpotlightCard spotlightColor="rgba(139, 92, 246, 0.2)">
            <div className="bg-gradient-to-br from-[rgba(30,30,46,0.9)] via-[rgba(30,30,46,0.8)] to-[rgba(46,46,69,0.5)] border border-white/10 ">
              <OverallScoreCard
                overallScore={sessionData?.overallScore}
                sessionType={sessionData?.sessionType}
                duration={sessionData?.duration}
                completedAt={sessionData?.completedAt}
                isLoading={isLoading}
              />
            </div>
          </SpotlightCard>

          {/* Action Bar */}
          <SpotlightCard spotlightColor="rgba(139, 92, 246, 0.15)">
            <div className="bg-gradient-to-br from-[rgba(30,30,46,0.9)] via-[rgba(30,30,46,0.8)] to-[rgba(46,46,69,0.5)] border border-white/10  p-4">
              <div className="flex flex-col sm:flex-row items-center justify-between space-y-4 sm:space-y-0">
                <div className="flex items-center space-x-4">
                  <div className="flex items-center space-x-2 text-sm text-gray-400">
                    <Icon name="Calendar" size={16} />
                    <span>Session ID: {sessionData?.sessionId}</span>
                  </div>
                  <div className="hidden sm:block w-px h-4 bg-gray-600"></div>
                  <div className="flex items-center space-x-2 text-sm text-gray-400">
                    <Icon name="Clock" size={16} />
                    <span>Completed: {sessionData?.completedAt?.toLocaleDateString()}</span>
                  </div>
                </div>

                <div className="flex items-center space-x-3">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleShareResults}
                    iconName="Share2"
                    className="border-gray-600 hover:border-gray-500 text-gray-300 hover:text-white"
                  >
                    Share
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleDownloadReport}
                    iconName="Download"
                    className="border-gray-600 hover:border-gray-500 text-gray-300 hover:text-white"
                  >
                    Download PDF
                  </Button>
                  <Button
                    variant="default"
                    size="sm"
                    onClick={handleRetakeInterview}
                    iconName="RotateCcw"
                    className="bg-gradient-to-r from-purple-500 to-indigo-600 hover:from-purple-600 hover:to-indigo-700"
                  >
                    Retake Interview
                  </Button>
                </div>
              </div>
            </div>
          </SpotlightCard>

          {/* Navigation Tabs */}
          <SpotlightCard spotlightColor="rgba(139, 92, 246, 0.15)">
            <div className="bg-gradient-to-br from-[rgba(30,30,46,0.9)] via-[rgba(30,30,46,0.8)] to-[rgba(46,46,69,0.5)] border border-white/10  overflow-hidden">
              <div className="flex overflow-x-auto">
                {tabs?.map((tab) => (
                  <button
                    key={tab?.id}
                    onClick={() => setActiveTab(tab?.id)}
                    className={`
                      flex items-center space-x-2 px-6 py-4 text-sm font-medium whitespace-nowrap
                      transition-all duration-200 border-b-2
                      ${activeTab === tab?.id
                        ? 'text-indigo-400 border-indigo-400 bg-indigo-500/5' 
                        : 'text-gray-400 border-transparent hover:text-white hover:bg-white/5'
                      }
                    `}
                  >
                    <Icon name={tab?.icon} size={16} />
                    <span>{tab?.label}</span>
                  </button>
                ))}
              </div>
            </div>
          </SpotlightCard>

          {/* Tab Content */}
          <div className="animate-fade-in">
            {renderTabContent()}
          </div>

          {/* Mobile Bottom Spacing */}
          <div className="h-20 md:hidden"></div>
        </div>
      </main>

      {/* Share Modal */}
      {showShareModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={() => setShowShareModal(false)} />
          <SpotlightCard spotlightColor="rgba(139, 92, 246, 0.2)">
            <div className="relative bg-gradient-to-br from-[rgba(30,30,46,0.95)] via-[rgba(30,30,46,0.9)] to-[rgba(46,46,69,0.6)] border border-white/10  p-6 max-w-md w-full animate-fade-in backdrop-blur">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-lg font-bold text-white">Share Your Results</h3>
                <button
                  onClick={() => setShowShareModal(false)}
                  className="text-gray-400 hover:text-white transition-colors duration-200"
                >
                  <Icon name="X" size={20} />
                </button>
              </div>

              <div className="space-y-4">
                <div className="p-4 bg-indigo-500/10 rounded-lg border border-indigo-500/20 text-center">
                  <Icon name="Trophy" size={32} className="text-indigo-400 mx-auto mb-2" />
                  <h4 className="font-semibold text-white">Interview Score: {sessionData?.overallScore}/100</h4>
                  <p className="text-sm text-gray-300 mt-1">
                    {sessionData?.overallScore >= 80 ? 'Excellent Performance!' : 'Good Progress!'}
                  </p>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <Button variant="outline" fullWidth iconName="Twitter" className="border-gray-600 text-gray-300 hover:text-white">
                    Twitter
                  </Button>
                  <Button variant="outline" fullWidth iconName="Linkedin" className="border-gray-600 text-gray-300 hover:text-white">
                    LinkedIn
                  </Button>
                  <Button variant="outline" fullWidth iconName="Facebook" className="border-gray-600 text-gray-300 hover:text-white">
                    Facebook
                  </Button>
                  <Button variant="outline" fullWidth iconName="Link" className="border-gray-600 text-gray-300 hover:text-white">
                    Copy Link
                  </Button>
                </div>
              </div>
            </div>
          </SpotlightCard>
        </div>
      )}
      
      <QuickActionButton onQuickStart={handleRetakeInterview} />
    </AppBackground>
  );
};

export default InterviewResultsFeedback;
